/**
 * @module utils/chartSettingsBridge
 * @description 在 ChartSettingsProvider 外（如 getTaskDetail callback）同步写入 Context BASE
 *
 * ## 为何存在 Provider 外桥接
 *
 * DVA `getTaskDetail`、自定义列 rename/delete 等发生在 React 树外，无法 `useDispatch`。
 * 本模块通过 `registerChartSettingsDispatch` / `registerChartSettingsState` 在 Provider mount 时
 * 注入引用，是唯一允许的「树外写 Context」入口。
 *
 * ## 对外 API（请仅使用下列导出）
 *
 * | 函数 | 用途 |
 * | --- | --- |
 * | `registerChartSettingsDispatch` / `unregisterChartSettingsDispatch` | Provider 生命周期注册 dispatch |
 * | `registerChartSettingsState` | Provider 注册 `getState`（rename/delete 读 base/draft） |
 * | `syncChartSettingsBaseFromModel` | 进页 / 详情回调：DVA → `SET_BASE_REPLACE` |
 * | `restoreChartSettingsFromCache` | session 恢复整包 PATCH |
 * | `resetChartSettingsSession` | 离页 / 新建：RESET + 清 bridge 签名 |
 * | `applyCustomColumnRenameOnQB` / `applyCustomColumnDeleteOnQB` | 自定义列改名/删列同步配置 |
 * | `applyDatasetFieldSortResetOnCustomColumnDelete` | 字段排序 A1：删自定义字段回默认 |
 * | `applyDatasetFieldSortResetIfUnresolved` | 字段排序 B1：换表/字段不可解析回默认 |
 * | `replaceChartSettingsBase` | 低层：带签名去重的 BASE 替换（优先用 sync* 封装） |
 *
 * 模块级 `dispatchRef` / `getStateRef` / `pendingPayload` 为内部实现，禁止外部直接访问。
 */

import type { Dispatch } from 'react';
import { getDvaApp } from 'umi';
import { CHART_OLD_NAMESPACE } from '@/pages/datalab/models/namespaces';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import type { TChartType } from '@dbl/core/chart';
import { CSActionType, INITIAL_STATE, type IChartSettingsState, type TCSAction } from '../types';
import {
  isChartSettingsSessionCache,
  type TChartSettingsSessionCache,
} from './chartSettingsSession';
import {
  migrateConfigLayerOnCustomColumnRename,
  type ICustomColumnRenameParams,
} from './customColumnConfigRename';
import type { IQBChangeSet, IQBLinkageContext } from '../linkage/qbFields/qbLinkage/types';
import { getQBLinkageHandlers } from '../linkage/qbFields/qbLinkage/registry';
import { runQBLinkagePipeline } from '../linkage/qbFields/qbLinkage/runPipeline';
import {
  buildBaseInitSignature,
  canInitializeBase,
  computeInitialBase,
  type IComputeInitialBaseInput,
} from './computeInitialBase';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import type { CustomColumn } from '@dbl/pages/Multidimensional/Analysis/interface';
import {
  buildDatasetFieldSortResetOnCustomDelete,
  buildDatasetFieldSortResetOnUnresolved,
} from '../settingGroups/common/SortByPanel/DatasetFieldSort/datasetFieldSortSync';

let dispatchRef: Dispatch<TCSAction> | null = null;
/** Provider 注册：model.updateCustomColumn 在 React 树外，需要读当前 base/draft 再生成 patch */
let getStateRef: (() => IChartSettingsState) | null = null;
/** Provider 未挂载时，SET_BASE_REPLACE 先入队，register 后 flush */
let pendingPayload: Record<string, unknown> | null = null;
/** Provider 未挂载时，session 恢复 PATCH 先入队，register 后 flush */
let pendingRestoreCache: Partial<IChartSettingsState> | null = null;
/** 进页 syncChartSettingsBaseFromModel 去重签名 */
let lastBridgeSignature = '';

/**
 * 注册 Context 状态读取器（与 registerChartSettingsDispatch 成对）
 * 用于 DVA effect 在改名时访问 field_setting / description 等是否存在于 base 或 draft
 */
export function registerChartSettingsState(
  getter: (() => IChartSettingsState) | null,
): void {
  getStateRef = getter;
}

export function registerChartSettingsDispatch(
  dispatch: Dispatch<TCSAction> | null,
): void {
  dispatchRef = dispatch;
  if (dispatch && pendingPayload) {
    dispatch({
      type: CSActionType.SET_BASE_REPLACE,
      payload: pendingPayload,
    });
    pendingPayload = null;
  }
  if (dispatch && pendingRestoreCache) {
    dispatch({
      type: CSActionType.PATCH,
      payload: pendingRestoreCache,
    });
    pendingRestoreCache = null;
  }
}

export function unregisterChartSettingsDispatch(): void {
  dispatchRef = null;
  getStateRef = null;
}

export type { ICustomColumnRenameParams };

/**
 * 自定义字段改名时**同步**写入 ChartSettings Context（MERGE_QB_PATCH）
 *
 * 调用时机：model.updateCustomColumn 在 put 之前。
 * 不经过 useQBLinkagePipeline 的原因见 customColumnConfigRename 模块头注释。
 * Provider 未挂载时（极少数进页竞态）静默跳过，仅 DVA save_column_infos 仍会由 model 侧迁移。
 */
export function applyCustomColumnRenameOnQB(
  params: ICustomColumnRenameParams,
): void {
  if (!dispatchRef || !getStateRef) {
    return;
  }
  const { oldColumnName, newColumnName } = params;
  if (
    !oldColumnName
    || !newColumnName
    || oldColumnName === newColumnName
  ) {
    return;
  }
  const state = getStateRef();
  const basePatch = migrateConfigLayerOnCustomColumnRename(state.base, params);
  const draftPatch = migrateConfigLayerOnCustomColumnRename(state.draft, params);
  if (!basePatch && !draftPatch) {
    return;
  }
  dispatchRef({
    type: CSActionType.MERGE_QB_PATCH,
    payload: {
      base: basePatch,
      draft: draftPatch,
    },
  });
}

/**
 * 删除自定义字段定义时**同步**清理 ChartSettings Context（MERGE_QB_PATCH）
 *
 * 调用时机：model.deleteConditionByCustomColumn 在 put 之前。
 * changeSet 由 customColumnConfigDelete.buildRemovalChangeSet 构造。
 */
export function applyCustomColumnDeleteOnQB(
  changeSet: IQBChangeSet,
  qb: IQBLinkageContext['qb'],
): void {
  if (!dispatchRef || !getStateRef) {
    return;
  }
  if (changeSet.events.size === 0) {
    return;
  }
  const state = getStateRef();
  const dvaState = getDvaApp()?._store?.getState?.() as
    | Record<string, { chartType?: string }>
    | undefined;
  const chartType = dvaState?.[CHART_OLD_NAMESPACE]?.chartType;
  const patch = runQBLinkagePipeline(
    changeSet,
    {
      base: state.base,
      draft: state.draft,
      chartType,
      qb,
    },
    getQBLinkageHandlers(),
  );
  if (
    !Object.keys(patch.base).length &&
    !Object.keys(patch.draft).length
  ) {
    return;
  }
  dispatchRef({
    type: CSActionType.MERGE_QB_PATCH,
    payload: {
      base: Object.keys(patch.base).length ? patch.base : undefined,
      draft: Object.keys(patch.draft).length ? patch.draft : undefined,
    },
  });
}

/**
 * A1：字段排序引用的自定义字段被删 → base+draft 同时回默认排序
 *
 * 与 `applyCustomColumnDeleteOnQB` 独立：自定义字段可能未进 QB，无 removalChangeSet 时也要执行。
 */
export function applyDatasetFieldSortResetOnCustomColumnDelete(
  deletedNames: readonly string[],
): void {
  if (!dispatchRef || !getStateRef || !deletedNames.length) {
    return;
  }
  const state = getStateRef();
  const both = buildDatasetFieldSortResetOnCustomDelete(
    state.base,
    state.draft,
    deletedNames,
  );
  if (!both) {
    return;
  }
  dispatchRef({
    type: CSActionType.MERGE_QB_PATCH,
    payload: both,
  });
}

/**
 * B1：字段排序引用在最新 dataTables(+customColumns) 解析不到 → base+draft 回默认
 *
 * 不做同名兜底。调用时机：`setTableInfo` 写入新表结构时（schema 已确认，传 ready=true）。
 */
export function applyDatasetFieldSortResetIfUnresolved(
  dataTables: IGetTableInfoDto[],
  customColumns: CustomColumn[],
  dataTablesSchemaReady = true,
): void {
  if (!dispatchRef || !getStateRef) {
    return;
  }
  const state = getStateRef();
  const both = buildDatasetFieldSortResetOnUnresolved(
    state.base,
    state.draft,
    dataTables || [],
    customColumns || [],
    dataTablesSchemaReady,
  );
  if (!both) {
    return;
  }
  dispatchRef({
    type: CSActionType.MERGE_QB_PATCH,
    payload: both,
  });
}

/** @deprecated 使用 applyCustomColumnRenameOnQB */
export function applyFieldSettingRenameOnCustomColumn(
  params: Omit<ICustomColumnRenameParams, 'builderType' | 'columnDisplay' | 'columnDisplayEn'> & {
    builderType?: ICustomColumnRenameParams['builderType'];
  },
): void {
  applyCustomColumnRenameOnQB({
    ...params,
    builderType: params.builderType ?? 'dimension',
  });
}

export function resetChartSettingsBridgeSession(): void {
  lastBridgeSignature = '';
  pendingPayload = null;
  pendingRestoreCache = null;
}

/**
 * 整页级重置 ChartSettings Context + bridge 会话。
 *
 * 调用时机（与 DVA resetState 对齐）：
 * - 离开拖拉拽页（Provider 卸载 cleanup）
 * - 再点菜单进入新建空白页（index.resetToBlankAddPage）
 *
 * 注意：图表顶栏切图走 applyChartTypeLinkage，不应调用本函数。
 */
export function resetChartSettingsSession(): void {
  if (dispatchRef) {
    dispatchRef({ type: CSActionType.RESET });
  }
  resetChartSettingsBridgeSession();
}

/**
 * 从 session 快照恢复 Context（登录失效 / isUserFailure 恢复）。
 *
 * 优先级高于 syncChartSettingsBaseFromModel：快照含 draft、面板开关等运行时态；
 * 无合法快照时由 index.restoreChartSettingsAfterDvaRestore fallback 到 DVA chartsCfgSave。
 */
export function restoreChartSettingsFromCache(
  cache: TChartSettingsSessionCache | unknown,
): boolean {
  if (!isChartSettingsSessionCache(cache)) {
    return false;
  }

  const payload: Partial<IChartSettingsState> = {
    ...INITIAL_STATE,
    ...cache,
  };

  if (!dispatchRef) {
    pendingRestoreCache = payload;
    return true;
  }

  dispatchRef({
    type: CSActionType.PATCH,
    payload,
  });
  return true;
}

export function isChartSettingsBridgeInitialized(signature: string): boolean {
  return signature !== '' && signature === lastBridgeSignature;
}

/**
 * 替换 Context BASE；相同 signature 时跳过
 * @returns 是否执行了写入（含入队 pending）
 */
export function replaceChartSettingsBase(
  payload: Record<string, unknown>,
  signature?: string,
): boolean {
  if (signature) {
    if (signature === lastBridgeSignature) {
      return false;
    }
    lastBridgeSignature = signature;
  }

  if (!dispatchRef) {
    pendingPayload = payload;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        '[chartSettingsBridge] dispatch not ready, BASE init queued',
      );
    }
    return true;
  }

  dispatchRef({
    type: CSActionType.SET_BASE_REPLACE,
    payload,
  });
  return true;
}

interface IAnalysisModelSlice {
  chartType: TChartType;
  save_column_infos: unknown;
  columnDimensions: unknown[];
  columnMetrics: unknown[];
  columnFilters?: unknown[];
  mode?: TChartCalculateMode;
}

export function readAnalysisModelForBaseInit(
  namespace: string = CHART_OLD_NAMESPACE,
): IComputeInitialBaseInput | null {
  const app = getDvaApp?.();
  const store = app?._store;
  if (!store) {
    return null;
  }
  const slice = store.getState()?.[namespace] as
    | IAnalysisModelSlice
    | undefined;
  if (!slice) {
    return null;
  }
  return {
    chartType: slice.chartType,
    saveColumnInfos: slice.save_column_infos,
    columnDimensions: slice.columnDimensions ?? [],
    columnMetrics: slice.columnMetrics ?? [],
    columnFilters: slice.columnFilters,
    mode: slice.mode ?? ChartCalculateMode.Aggregate,
  };
}

/**
 * 从 DVA 当前切片计算并写入 BASE（getTaskDetail callback、计算前调用）
 */
export function syncChartSettingsBaseFromModel(
  namespace: string = CHART_OLD_NAMESPACE,
): boolean {
  const input = readAnalysisModelForBaseInit(namespace);
  if (!input || !canInitializeBase(input)) {
    return false;
  }
  const signature = buildBaseInitSignature(input);
  const payload = computeInitialBase(input);
  return replaceChartSettingsBase(payload, signature);
}

